import { useDataTableContext } from '../../components/DataTable'
import useApiService from '../../services/api'
import lodash from 'lodash'
import confirm from '../../components/dialogConfirm'
import { TableTypeSlug } from 'models/companies'

interface CampaignUnassignerProps {
  accessorKey: string
  tableQueryKey: string[]
  requestParams: Record<string, any>
  campaignId: number
  data: any[]
  setPage: (page: number) => void
  totalCount: number
  pageSize?: number
}

export function useCampaignUnassigner(
  companyType: TableTypeSlug,
  props: CampaignUnassignerProps
) {
  const api = useApiService()
  const pageSize = props.pageSize || 100

  const mutation = api.useUnassignToCampaignMutation(
    companyType,
    props.tableQueryKey,
    props.accessorKey
  )

  const {
    state: {
      rowSelection,
      isAllRowsSelected,
      totalRowsInBackend,
      totalSelectedRows,
    },
  } = useDataTableContext()

  async function handleSubmit() {
    const selectedRows = props.data?.filter((_, i) => {
      return Boolean(rowSelection?.[i])
    })

    const selectedChainProxyIds = selectedRows?.map((r: any) =>
      lodash.get(r, props.accessorKey)
    ) as number[]

    const unselectedRows = props.data?.filter((_, i) => {
      return !rowSelection?.[i]
    })

    const unselectedChainProxyIds = unselectedRows?.map((r: any) =>
      lodash.get(r, props.accessorKey)
    ) as number[]

    const confirmed = await confirm(
      `Confirm unassigning ${totalSelectedRows} companies from campaign?`,
      'Unassign companies from Campaign'
    )

    if (!confirmed) {
      return
    }

    await mutation.mutateAsync({
      campaign_id: props.campaignId,
      include_ids: isAllRowsSelected ? [] : selectedChainProxyIds,
      exclude_ids: unselectedChainProxyIds,
      filter_params: props.requestParams,
    })

    // After mutation completes, calculate pagination updates
    const newTotal = Math.max(0, totalRowsInBackend - totalSelectedRows)
    const lastPossiblePage = Math.max(1, Math.ceil(newTotal / pageSize) - 1)

    // Get current page index
    const currentPageIndex = props.requestParams.page

    // Calculate if current page will be empty
    const itemsRemainingOnCurrentPage =
      props.data.length - selectedChainProxyIds.length
    const shouldChangePage =
      itemsRemainingOnCurrentPage === 0 || currentPageIndex > lastPossiblePage

    if (shouldChangePage) {
      // If we're removing all items from current page, go to previous page unless we're on first page
      const targetPage =
        itemsRemainingOnCurrentPage === 0 && currentPageIndex > 0
          ? currentPageIndex
          : lastPossiblePage

      // Update pagination state after mutation is complete
      props.setPage(targetPage)
    }
  }

  return {
    unassign: handleSubmit,
    isUnassigning: mutation.isPending,
  }
}
